<script setup lang="ts">
import CenterModal from "~/components/Common/Modals/CenterModal.vue";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";
import { usePagination } from "~/composables/paginations/usePagination";
import AreaInput from "~/components/Common/Inputs/AreaInput.vue";
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import DateInput from "~/components/Common/Inputs/DateInput.vue";
import { useBookingsStore } from "~/store/bookings/bookings.store";
import { AbsentReasons } from "~/utils/options/absent-reasons.options";
import { useSessionIndividual } from "~/composables/sessions/useSessionIndividual";
import { SessionStatusEnum } from "~/utils/enums/session-details-enums";
import { useFormats } from "~/composables/utils/useFormat";
import { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { useSessionDetailsAttendeesStore } from "~/store/sessions/session-details-attendees-store";
import AppButton from "~/components/Common/Buttons/AppButton.vue";

const onLoaded: Ref<boolean> = ref(false);
const emit = defineEmits(["hide", "notifConfirmModal"]);
const sessionDetailsStore = useSessionDetailsStore();
const sessionDetailsAttendeesStore = useSessionDetailsAttendeesStore();
const sessionStore = useSessionsStore();
const { trainingPlanSessionIndividual } = storeToRefs(sessionStore);
const sessionIndividual = useSessionIndividual();
const reason = ref<any>(null);
const date = ref(new Date());
const useBookings = useBookingsStore();
const usePaginate = usePagination();
const edit = ref(false);
const comment = ref("");
const formats = useFormats();
const paginateAttendees = usePagination(PaginationKeysEnum.ATTENDEES_PAGINATION);
const paginateUnscheduleUsers = usePagination(PaginationKeysEnum.UNSCHEDULE_USERS_PAGINATION);
function hideModal() {
  emit("hide");
}

onMounted(() => {
  if (sessionIndividual.getSessionHeading().status === SessionStatusEnum.NO_SHOW) {
    edit.value = true;
    reason.value = AbsentReasons.find((x) => x.value === trainingPlanSessionIndividual.value?.noShowReason) ?? null;
    date.value = trainingPlanSessionIndividual.value?.noShowDate
      ? formats.toDateDisplayNoUTC(trainingPlanSessionIndividual.value?.noShowDate)
      : new Date();
    comment.value = trainingPlanSessionIndividual.value?.noShowDesc ?? "";
  }
});

const doneModal = async () => {
  hideModal();
  if (edit.value) {
    await useBookings.editEndOfTraining({
      noShowReason: reason.value?.name,
      noShowDate: date.value,
      noShowDesc: comment.value,
    });
  } else {
    await useBookings.setEndOfTraining({
      manualDuration: 0,
      noShowReason: reason.value?.name,
      noShowDate: date.value,
      noShowDesc: comment.value,
    });
  }

  if (!sessionDetailsStore.sessionViewModal) {
    await usePaginate.fetchData();
    /**
     * NEED TO FIX later hack for tabnavigation
     * Reload data on the same tab
     * refacto: Need to redefine tab location after event
     */
    if (document.querySelector(".nav_selected")) {
      (document.querySelector(".nav_selected") as HTMLInputElement).click();
    }
  } else {
    await sessionDetailsStore.fetchSessionDetails(
      sessionDetailsStore.sessionDetails?.sessionId!,
      sessionDetailsStore.sessionDetails?.itemType!,
    );
    const paginationOptionsAttendees = paginateAttendees.getFindOptions(paginateAttendees.paginateOptions.value);
    const paginationOptionsUnscheduleUsers = paginateUnscheduleUsers.getFindOptions(
      paginateUnscheduleUsers.paginateOptions.value,
    );
    await sessionDetailsAttendeesStore.fetchSessionAttendeesFullDatas(
      sessionDetailsStore.sessionDetails?.sessionId!,
      sessionDetailsStore.sessionDetails?.itemType!,
      paginationOptionsAttendees,
    );
    await sessionDetailsAttendeesStore.fetchSessionUnscheduleUsers(
      sessionDetailsStore.sessionDetails?.sessionId!,
      sessionDetailsStore.sessionDetails?.itemType!,
      paginationOptionsUnscheduleUsers,
    );
    sessionDetailsAttendeesStore.resetIndexGrid();
  }
};
</script>

<template>
  <CenterModal :data-responsive="true" style="max-width: 720px; max-height: 772px; width: 100%" @hide="hideModal">
    <template #body>
      <div class="flex flex-col h-full">
        <HeaderDetails :with-shadow="false" back-icon="arrow_left" :close="false" back @backClick="hideModal">
        </HeaderDetails>
        <div class="flex gap-[8px] overflow-auto body_regular w-full overflow-y-scroll h-full">
          <div class="flex flex-col gap-6 mx-[64px] py-[52px]">
            <h3 class="heading_3_demi">Déclaration d'absence</h3>
            <ul class="list-disc pl-4 flex flex-col gap-4">
              <li>Si cette formation délivre une habilitation, celle-ci sera invalidée.</li>
              <li>
                L'inscription restera visible dans votre plan de formation et les dépenses associées seront décomptées
                de votre budget.
              </li>
              <li>Vous pouvez sinon annuler l'inscription ou changer le participant de session.</li>
            </ul>
            <div class="flex gap-2">
              <div class="w-full">
                <p class="body_regular text-secondary-color pb-2">Motif d’absence</p>
                <SimpleDropdown
                  v-model="reason"
                  searchable
                  class="max-w-[416px]"
                  label="value"
                  placeholder="Sélectionner un motif"
                  :options="AbsentReasons"
                  track-by="value"
                  :hide-selected="true"
                  :nb_Element="4"
                >
                </SimpleDropdown>
              </div>
              <div class="w-full">
                <p class="body_regular text-secondary-color pb-2">Date d’annonce de l’absence</p>
                <DateInput v-model="date" placeholder="Choisir" :teleport="true"></DateInput>
              </div>
            </div>
            <div>
              <p class="body_regular text-secondary-color pb-2">Commentaire :</p>
              <AreaInput
                v-model="comment"
                class="textarea__input overflow-hidden !min-h-[90px]"
                placeholder="Ajouter un commentaire"
              ></AreaInput>
            </div>
          </div>
        </div>
        <footer class="invite_step_date__footer_container">
          <AppButton type="outlined" label="Retour" class="w-full" @click="hideModal" />
          <AppButton label="Valider" class="w-full" @click="doneModal()" />
        </footer>
      </div>
    </template>
  </CenterModal>
</template>

<style scoped lang="scss">
.text-area {
  @apply min-h-[80px];
}

.svg_button__button {
  @apply w-[24px] h-[24px];
  color: var(--text-secondary-color);
  cursor: pointer;
}

.invite_step_date__footer_container {
  @apply flex items-center px-[64px] w-full flex-row justify-between gap-[16px];
  height: 68px;
  min-height: 68px;
  border-top: 1px solid var(--grey-lightest);
}

@media (max-width: 768px) {
  .invite_step_date__footer_container {
    @apply flex flex-col gap-[8px] px-[24px] py-[16px];
    min-height: 0px;
    height: auto;
  }
}
</style>
